import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Props {
  title: string;
  /** Initial open state. The section is uncontrolled after mount. */
  defaultOpen?: boolean;
  /**
   * Rendered inline next to the title, inside the trigger, so the key facts
   * (e.g. Approved / Declined) stay visible while the body is collapsed.
   */
  summary?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A result card whose header toggles its body. Every section on the AI
 * Analysis tab uses this so the page reads as a short list of headings that
 * open on demand rather than a wall of tables.
 */
const CollapsibleSection: React.FC<Props> = ({ title, defaultOpen = false, summary, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="p-4">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-expanded={open}
            className="flex w-full flex-wrap items-center gap-3 text-left"
          >
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
            {summary}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3">{children}</div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default CollapsibleSection;
